import React, { useState } from 'react';
import {
  Download,
  Building2,
  Phone,
  Globe,
  MapPin,
  Star,
  ExternalLink,
  Layers,
  FileSpreadsheet,
  Check,
  Copy
} from 'lucide-react';
import { exportToExcel } from '../utils/api';
import { useToast } from '../hooks/useToast';

export default function ResultsTable({ results, query, category, searchedAt }) {
  const toast = useToast();
  const [downloading, setDownloading] = useState(false);
  const [copiedPhone, setCopiedPhone] = useState(null);

  if (!results || results.length === 0) return null;

  const handleExport = async () => {
    setDownloading(true);
    try {
      const res = await exportToExcel({
        leads: results,
        query,
        category: category || 'General'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `LeadForge_${(query || 'leads').replace(/[^a-zA-Z0-9]/g, '_')}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast(`Successfully downloaded ${results.length} leads to Excel!`, 'success');
    } catch (err) {
      toast('Excel export failed. Please try again.', 'error');
    } finally {
      setDownloading(false);
    }
  };

  const copyToClipboard = (text, type) => {
    navigator.clipboard.writeText(text);
    setCopiedPhone(text);
    toast(`Copied ${type} to clipboard`, 'success');
    setTimeout(() => setCopiedPhone(null), 2000);
  };

  const withPhone = results.filter(r => r.phoneNumber).length;
  const withWebsite = results.filter(r => r.website).length;
  const avgRating = results.filter(r => r.rating).length > 0
    ? (results.filter(r => r.rating).reduce((a, b) => a + parseFloat(b.rating || 0), 0) / results.filter(r => r.rating).length).toFixed(1)
    : null;

  return (
    <div className="results-card">
      {/* Header */}
      <div className="results-header">
        <div className="results-title-group">
          <Layers size={20} color="var(--color-indigo)" />
          <h2 className="results-title">Extracted Leads</h2>
          <span className="results-count">
            Showing all {results.length} leads
          </span>
        </div>

        <div className="results-actions">
          <button
            id="export-excel-btn"
            className="btn btn-primary"
            onClick={handleExport}
            disabled={downloading}
          >
            {downloading ? (
              <>
                <span className="spinner" />
                Exporting...
              </>
            ) : (
              <>
                <FileSpreadsheet size={16} />
                Download Excel ({results.length} Leads)
              </>
            )}
          </button>
        </div>
      </div>

      {/* Stats bar */}
      <div className="stats-strip">
        <div className="stat-item">
          <span>Query:</span>
          <strong>{query}</strong>
        </div>
        <div className="stat-item">
          <Phone size={14} color="var(--text-muted)" />
          <span>With Phone:</span>
          <strong>{withPhone} / {results.length}</strong>
        </div>
        <div className="stat-item">
          <Globe size={14} color="var(--text-muted)" />
          <span>With Website:</span>
          <strong>{withWebsite} / {results.length}</strong>
        </div>
        {avgRating && (
          <div className="stat-item">
            <Star size={14} color="var(--color-warning)" />
            <span>Avg Rating:</span>
            <strong>{avgRating} ★</strong>
          </div>
        )}
      </div>

      {/* Table displaying all leads */}
      <div className="table-responsive">
        <table>
          <thead>
            <tr>
              <th style={{ width: 40, textAlign: 'center' }}>#</th>
              <th>Company Name</th>
              <th>Category</th>
              <th>Rating & Reviews</th>
              <th>Phone Number</th>
              <th>Website</th>
              <th>Address</th>
              <th style={{ textAlign: 'center' }}>Maps</th>
            </tr>
          </thead>
          <tbody>
            {results.map((lead, idx) => (
              <tr key={idx}>
                <td style={{ textAlign: 'center', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>
                  {idx + 1}
                </td>
                <td className="td-strong">
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <Building2 size={15} color="var(--color-indigo)" style={{ flexShrink: 0 }} />
                    <span>{lead.companyName || '—'}</span>
                  </div>
                </td>
                <td>
                  <span className="badge badge-indigo">
                    {lead.category || lead.type || 'Business'}
                  </span>
                </td>
                <td>
                  {lead.rating ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                      <Star size={14} fill="#f59e0b" color="#f59e0b" />
                      <strong style={{ color: 'var(--text-primary)' }}>{lead.rating}</strong>
                      {lead.reviews && (
                        <span style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>
                          ({lead.reviews})
                        </span>
                      )}
                    </div>
                  ) : (
                    <span style={{ color: 'var(--text-muted)' }}>—</span>
                  )}
                </td>
                <td>
                  {lead.phoneNumber ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                      <a
                        href={`tel:${lead.phoneNumber}`}
                        style={{ color: 'var(--text-primary)', fontFamily: 'var(--font-mono)' }}
                      >
                        {lead.phoneNumber}
                      </a>
                      <button
                        type="button"
                        onClick={() => copyToClipboard(lead.phoneNumber, 'phone number')}
                        style={{ background: 'none', border: 'none', color: 'var(--text-muted)', display: 'flex', padding: 2 }}
                        title="Copy phone"
                      >
                        {copiedPhone === lead.phoneNumber ? <Check size={13} color="var(--color-success)" /> : <Copy size={13} />}
                      </button>
                    </div>
                  ) : (
                    <span style={{ color: 'var(--text-muted)' }}>—</span>
                  )}
                </td>
                <td>
                  {lead.website ? (
                    <a
                      href={lead.website.startsWith('http') ? lead.website : `https://${lead.website}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: 'var(--color-indigo)', display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}
                      title={lead.website}
                    >
                      <Globe size={13} />
                      <span>{lead.website.replace(/^https?:\/\//, '').split('/')[0]}</span>
                      <ExternalLink size={12} />
                    </a>
                  ) : (
                    <span style={{ color: 'var(--text-muted)' }}>—</span>
                  )}
                </td>
                <td style={{ maxWidth: 280, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={lead.address}>
                  {lead.address || '—'}
                </td>
                <td style={{ textAlign: 'center' }}>
                  {lead.googleMapsLink ? (
                    <a
                      href={lead.googleMapsLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-secondary btn-sm"
                      style={{ padding: '0.25rem 0.5rem', display: 'inline-flex' }}
                      title="View on Google Maps"
                    >
                      <MapPin size={13} color="var(--color-indigo)" />
                    </a>
                  ) : (
                    '—'
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
